import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Button, Table, Progress, message, Tabs, Modal, Space, Tag, Upload, Input, Select, Radio } from 'antd';
import { PlayCircleOutlined, VideoCameraOutlined, DeleteOutlined, ArrowLeftOutlined, CheckCircleOutlined, ImportOutlined, ExportOutlined, FileExcelOutlined, FileTextOutlined, UploadOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { shotApi, aiApi, scriptApi, projectApi, api, assetApi } from '../services/api';

const { TabPane } = Tabs;
const { Option } = Select;

const StoryboardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shots, setShots] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeEpisode, setActiveEpisode] = useState('all');
  const [episodes, setEpisodes] = useState([]);
  const [selectedShots, setSelectedShots] = useState([]);
  const [script, setScript] = useState('');

  // 导入导出相关状态
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  // 视频生成相关状态
  const [videoConfirmVisible, setVideoConfirmVisible] = useState(false);
  const [currentShot, setCurrentShot] = useState(null);
  const [videoConfig, setVideoConfig] = useState({
    prompt: '',
    aspectRatio: '16:9',
    duration: '6',
    resolution: '720P',
    referenceImage: null,
    referenceAssetId: null,
    referenceSource: 'none',  // 'none', 'asset', 'upload'
    videoProvider: '',  // 视频提供商，空则使用系统默认
    referenceVideoUrl: '',  // 参考视频URL（MiniMax）
    referenceAudioUrl: ''   // 参考音频URL（MiniMax）
  });
  const [project, setProject] = useState(null);
  
  // 资产列表（用于参考图选择）
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  
  // 分镜图生成相关状态
  const [storyboardImageModalVisible, setStoryboardImageModalVisible] = useState(false);
  const [storyboardImageConfig, setStoryboardImageConfig] = useState({
    prompt: '',
    aspectRatio: '16:9',
    referenceAssets: [], // 多选资产 [{ assetId, asset, roleName }]
    referenceSource: 'none'  // 'none', 'asset'
  });
  const [storyboardImagePreview, setStoryboardImagePreview] = useState(null);
  
  const saveStoryboardConfig = useCallback((config) => {
    setStoryboardImageConfig(config);
    if (id && currentShot) {
      localStorage.setItem(`storyboard_config_${id}_${currentShot.id}`, JSON.stringify({
        prompt: config.prompt,
        aspectRatio: config.aspectRatio,
        referenceAssets: config.referenceAssets,
        referenceSource: config.referenceSource
      }));
    }
  }, [id, currentShot]);
  
  // 视频预览相关状态
  const [isVideoPreviewVisible, setIsVideoPreviewVisible] = useState(false);
  const [previewVideo, setPreviewVideo] = useState(null);
  
  // 视频上传相关状态
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadingShot, setUploadingShot] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = React.useRef(null);

  // 获取完整的视频URL
  const getFullVideoUrl = (videoPath) => {
    if (!videoPath) return '';
    // 如果是完整的URL（以http开头），直接返回
    if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
      return videoPath;
    }
    // 如果是 data/ 开头的路径，需要添加 /shortdrama 前缀
    if (videoPath.startsWith('data/')) {
      try {
        return '/shortdrama/' + videoPath.split('/').map(encodeURIComponent).join('/');
      } catch (e) {
        return '/shortdrama/' + videoPath;
      }
    }
    // 如果已经有 /shortdrama 前缀，直接返回
    if (videoPath.startsWith('/shortdrama/')) {
      try {
        // 对中文路径进行编码
        const parts = videoPath.replace('/shortdrama/', '').split('/');
        return '/shortdrama/' + parts.map(encodeURIComponent).join('/');
      } catch (e) {
        return videoPath;
      }
    }
    // 如果是相对路径，确保以 / 开头
    if (!videoPath.startsWith('/')) {
      return '/' + videoPath;
    }
    return videoPath;
  };

  // 获取完整的图片URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // 如果是完整的URL（以http开头），使用后端代理
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return `/shortdrama/api/ai/proxy-image?url=${encodeURIComponent(imagePath)}`;
    }
    // 如果是 data/ 开头的路径，需要添加 /shortdrama 前缀
    if (imagePath.startsWith('data/')) {
      try {
        return '/shortdrama/' + imagePath.split('/').map(encodeURIComponent).join('/');
      } catch (e) {
        return '/shortdrama/' + imagePath;
      }
    }
    // 如果已经有 /shortdrama 前缀，直接返回
    if (imagePath.startsWith('/shortdrama/')) {
      try {
        // 对中文路径进行编码
        const parts = imagePath.replace('/shortdrama/', '').split('/');
        return '/shortdrama/' + parts.map(encodeURIComponent).join('/');
      } catch (e) {
        return imagePath;
      }
    }
    // 如果是相对路径，确保以 / 开头
    if (!imagePath.startsWith('/')) {
      return '/' + imagePath;
    }
    return imagePath;
  };

  // 处理视频预览
  const handleVideoPreview = (record) => {
    console.log(`[handleVideoPreview] 打开视频预览:`, record);
    console.log(`[handleVideoPreview] video_path:`, record.video_path);
    console.log(`[handleVideoPreview] 完整URL:`, getFullVideoUrl(record.video_path));
    setPreviewVideo(record);
    setIsVideoPreviewVisible(true);
  };

  const handleCancelVideoPreview = () => {
    setIsVideoPreviewVisible(false);
    setPreviewVideo(null);
  };

  // 处理视频上传
  const handleUploadVideo = (record) => {
    setUploadingShot(record);
    setIsUploadModalVisible(true);
    setUploadFile(null);
    // 清空文件输入框
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadingShot) return;

    try {
      const formData = new FormData();
      formData.append('video', uploadFile);
      formData.append('shot_id', uploadingShot.id);
      formData.append('project_id', id);

      // 调用上传视频的API
      const response = await shotApi.uploadVideo(id, uploadingShot.index, formData);
      
      message.success('视频上传成功');
      
      // 重新加载分镜数据
      loadShots();
      
      // 关闭模态框
      setIsUploadModalVisible(false);
      setUploadingShot(null);
      setUploadFile(null);
      // 清空文件输入框
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      message.error('视频上传失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelUpload = () => {
    setIsUploadModalVisible(false);
    setUploadingShot(null);
    setUploadFile(null);
    // 清空文件输入框
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 处理视频删除
  const handleDeleteVideo = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除第${record.index}镜的视频吗？`,
      onOk: async () => {
        try {
          // 调用删除视频的API
          await shotApi.deleteVideo(id, record.index);
          
          message.success('视频删除成功');
          
          // 重新加载分镜数据
          loadShots();
        } catch (error) {
          message.error('视频删除失败: ' + (error.response?.data?.detail || error.message));
        }
      },
      onCancel: () => {}
    });
  };

  // 从剧本内容中解析集数
  const parseEpisodesFromScript = (script) => {
    const episodes = [];
    const episodePattern = /^\s*第([一二三四五六七八九十\d]+)集[:：]?\s*$/gm;
    let match;
    
    while ((match = episodePattern.exec(script)) !== null) {
      const episodeNum = match[1];
      episodes.push(`第${episodeNum}集`);
    }
    
    // 如果没有分集标记，将整个剧本作为第一集
    if (episodes.length === 0 && script.trim()) {
      episodes.push('第1集');
    }
    
    return episodes;
  };

  // 从剧本内容中解析场景数量
  const parseScenesFromScript = (script) => {
    // 匹配场景标题格式：如 "56-1 训练馆 夜/内" 或 "场景1" 等
    const scenePattern = /(?:^|\n)\s*(?:\d+-\d+\s+\S+|场景\d+|SCENE\s*\d+|第[一二三四五六七八九十\d]+场(?!集))\s*[\s\-]/gi;
    // 匹配集数标题：如 "第X集"，这些标题应该被跳过
    const episodePattern = /^\s*第[一二三四五六七八九十\d]+集\s*$/gi;
    
    const scenes = [];
    const lines = script.split('\n');
    let inEpisode = false;
    let currentEpisode = null;
    
    for (const line of lines) {
      // 检查是否是集数标题
      if (episodePattern.test(line)) {
        inEpisode = true;
        currentEpisode = line.trim();
        continue;
      }
      
      // 如果在某一集内，检查是否是场景标题
      if (inEpisode && scenePattern.test(line)) {
        scenes.push({ episode: currentEpisode, scene: line.trim() });
      }
    }
    
    // 如果没有识别到场景，将整个剧本作为一个场景
    if (scenes.length === 0 && script.trim()) {
      scenes.push({ episode: '第1集', scene: '场景1' });
    }
    
    return scenes;
  };

  // 使用 ref 存储 startPollingVideoStatus 函数，避免循环依赖
  const startPollingVideoStatusRef = useRef(null);
  
  // 使用 ref 存储 startPollingStoryboardImageStatus 函数，避免循环依赖
  const startPollingStoryboardImageStatusRef = useRef(null);
  
  // 使用 ref 存储 shots 状态，以便在异步操作中获取最新值
  const shotsRef = useRef([]);
  
  // 同步 shots 状态到 ref
  useEffect(() => {
    shotsRef.current = shots;
  }, [shots]);

  const loadShots = useCallback(async () => {
    console.log(`[loadShots] 开始加载分镜数据, project_id=${id}`);
    try {
      const response = await shotApi.getAll(id);
      const shotsData = response.data;
      console.log(`[loadShots] 获取到 ${shotsData.length} 个分镜`);
      
      // 检查每个分镜的视频状态
      shotsData.forEach(shot => {
        console.log(`[loadShots] shot ${shot.index}: video_status=${shot.video_status}, video_path=${shot.video_path ? '有' : '无'}, video_task_id=${shot.video_task_id || '无'}`);
      });
      
      // 合并服务器数据和本地状态
      setShots(prevShots => {
        return shotsData.map(serverShot => {
          // 优先使用id匹配，如果没有id则使用index匹配
          const localShot = prevShots.find(s => 
            (s.id && serverShot.id) ? s.id === serverShot.id : s.index === serverShot.index
          );
          if (localShot) {
            const mergedShot = { ...serverShot };
            
            // 如果服务器状态是pending而本地状态是generating，使用本地状态（任务可能还在进行中）
            if (localShot.video_status === 'generating' && serverShot.video_status === 'pending') {
              console.log(`[loadShots] 保留本地视频generating状态 for shot ${serverShot.index}`);
              mergedShot.video_status = 'generating';
              mergedShot.video_task_id = localShot.video_task_id || serverShot.video_task_id;
            }
            // 如果本地有外部URL而服务器没有，保留本地URL
            if (localShot.video_path && localShot.video_path.startsWith('http') && 
                (!serverShot.video_path || !serverShot.video_path.startsWith('http'))) {
              console.log(`[loadShots] 保留外部视频URL for shot ${serverShot.index}`);
              mergedShot.video_path = localShot.video_path;
              mergedShot.video_urls = localShot.video_urls || serverShot.video_urls;
            }
            // 如果服务器分镜图状态是pending而本地状态是generating，使用本地状态
            if (localShot.storyboard_image_status === 'generating' && serverShot.storyboard_image_status === 'pending') {
              console.log(`[loadShots] 保留本地分镜图generating状态 for shot ${serverShot.index}`);
              mergedShot.storyboard_image_status = 'generating';
              mergedShot.storyboard_image_task_id = localShot.storyboard_image_task_id || serverShot.storyboard_image_task_id;
            }
            // 如果本地分镜图有外部URL而服务器没有，保留本地URL
            if (localShot.storyboard_image_path && localShot.storyboard_image_path.startsWith('http') && 
                (!serverShot.storyboard_image_path || !serverShot.storyboard_image_path.startsWith('http'))) {
              console.log(`[loadShots] 保留外部分镜图URL for shot ${serverShot.index}`);
              mergedShot.storyboard_image_path = localShot.storyboard_image_path;
            }
            
            return mergedShot;
          }
          return serverShot;
        });
      });
      
      // 从剧本内容中解析所有集数
      if (script) {
        const episodesFromScript = parseEpisodesFromScript(script);
        setEpisodes(episodesFromScript);
      } else {
        // 如果没有剧本内容，从现有分镜中提取集数
        const uniqueEpisodes = [...new Set(shotsData.map(s => s.episode))];
        setEpisodes(uniqueEpisodes);
      }
      
      // 检查是否有正在生成的视频或分镜图任务，恢复轮询
      // 注意：这里使用 setTimeout 延迟执行，确保 ref 已经设置
      setTimeout(() => {
        let pendingVideoTasks = 0;
        let pendingImageTasks = 0;
        // 使用当前的shots状态（已经合并了服务器和本地数据）
        const currentShots = shotsRef.current || [];
        currentShots.forEach(shot => {
          if (shot.video_status === 'generating' && shot.video_task_id) {
            pendingVideoTasks++;
            console.log(`[loadShots] 发现未完成视频任务: shot_index=${shot.index}, task_id=${shot.video_task_id}`);
            // 使用 ref 调用函数，避免循环依赖
            if (startPollingVideoStatusRef.current) {
              console.log(`[loadShots] 调用startPollingVideoStatus恢复轮询`);
              startPollingVideoStatusRef.current(shot.video_task_id, shot.index);
            } else {
              console.error(`[loadShots] startPollingVideoStatusRef.current 为空!`);
            }
          }
          if (shot.storyboard_image_status === 'generating' && shot.storyboard_image_task_id) {
            pendingImageTasks++;
            console.log(`[loadShots] 发现未完成分镜图任务: shot_index=${shot.index}, task_id=${shot.storyboard_image_task_id}`);
            // 使用 ref 调用函数，避免循环依赖
            if (startPollingStoryboardImageStatusRef.current) {
              console.log(`[loadShots] 调用startPollingStoryboardImageStatus恢复轮询`);
              startPollingStoryboardImageStatusRef.current(shot.storyboard_image_task_id, shot.id);
            } else {
              console.error(`[loadShots] startPollingStoryboardImageStatusRef.current 为空!`);
            }
          }
        });
        console.log(`[loadShots] 发现 ${pendingVideoTasks} 个未完成视频任务, ${pendingImageTasks} 个未完成分镜图任务`);
      }, 100);
    } catch (error) {
      console.error('[loadShots] 加载分镜失败:', error);
      message.error('加载分镜失败');
    }
  }, [id, script]);

  const loadScript = useCallback(async () => {
    try {
      const response = await scriptApi.get(id);
      const scriptContent = response.data.content || '';
      setScript(scriptContent);
      
      // 从剧本内容中解析所有集数
      const episodesFromScript = parseEpisodesFromScript(scriptContent);
      setEpisodes(episodesFromScript);
    } catch (error) {
      console.error('加载剧本失败:', error);
      setScript('');
    }
  }, [id]);

  // 加载项目信息
  const loadProject = useCallback(async () => {
    try {
      const projectRes = await projectApi.getById(id);
      setProject(projectRes.data);
    } catch (error) {
      console.error('加载项目信息失败:', error);
    }
  }, [id]);

  // 加载资产列表（用于参考图选择）
  const loadAssets = useCallback(async () => {
    try {
      setAssetsLoading(true);
      const response = await assetApi.getAll(id);
      setAssets(response.data || []);
    } catch (error) {
      console.error('加载资产失败:', error);
    } finally {
      setAssetsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadShots();
      loadScript();
      loadProject();
    }
  }, [id, loadShots, loadScript, loadProject]);

  // 检查是否存在分镜
  const checkExistingShots = async () => {
    try {
      const response = await shotApi.getAll(id);
      return response.data && response.data.length > 0;
    } catch (error) {
      console.error('检查分镜失败:', error);
      return false;
    }
  };

  const handleGenerateStoryboard = async () => {
    // 检查是否已存在分镜
    const hasShots = await checkExistingShots();
    
    // 根据当前选择的分集构建确认消息
    const episodeText = activeEpisode === 'all' ? '全部集数' : activeEpisode;
    
    if (hasShots) {
      Modal.confirm({
        title: '确认操作',
        content: `数据库里已经存在分镜，是否重新生成${episodeText}的分镜？此操作将会覆盖${episodeText}的现有数据。`,
        okText: '确认执行',
        cancelText: '取消',
        onOk: () => {
          // 立即执行，不使用 await，确保模态框立即关闭
          executeGenerateStoryboard();
        }
      });
    } else {
      executeGenerateStoryboard();
    }
  };

  const executeGenerateStoryboard = async () => {
    if (!script || script.trim().length === 0) {
      message.warning('请先输入或上传剧本内容');
      return;
    }
    
    setIsGenerating(true);
    setProgress(0);
    let statusInterval = null;
    
    try {
      console.log('=== 开始生成分镜 ===');
      console.log(`生成范围: ${activeEpisode === 'all' ? '全部集数' : activeEpisode}`);
      console.log(`项目ID: ${id}`);
      console.log(`剧本长度: ${script.length} 字符`);
      
      // 启动分镜生成任务
      console.log('发送启动分镜生成任务请求...');
      const startResponse = await aiApi.startGenerateStoryboard(script, id, '', activeEpisode);
      console.log('=== 分镜生成任务启动响应 ===');
      console.log('响应状态码:', startResponse.status);
      console.log('响应数据:', startResponse.data);
      
      if (!startResponse.data || !startResponse.data.success) {
        console.error('启动分镜生成任务失败:', startResponse.data?.error);
        message.error(startResponse.data?.error || '启动分镜生成任务失败');
        setIsGenerating(false);
        return;
      }
      
      const taskId = startResponse.data.task_id;
      const totalScenes = startResponse.data.total_scenes;
      console.log(`分镜生成任务ID: ${taskId}`);
      console.log(`总场景数: ${totalScenes}`);
      
      // 轮询任务状态
      console.log('开始轮询任务状态...');
      statusInterval = setInterval(async () => {
        try {
          console.log(`轮询任务状态: ${taskId}`);
          const statusResponse = await aiApi.getStoryboardStatus(taskId);
          console.log('=== 分镜生成任务状态响应 ===');
          console.log('响应状态码:', statusResponse.status);
          console.log('响应数据:', statusResponse.data);
          
          const status = statusResponse.data.status;
          const progress = statusResponse.data.progress;
          const error = statusResponse.data.error;
          
          console.log(`任务状态: ${status}`);
          console.log(`任务进度: ${JSON.stringify(progress)}`);
          console.log(`任务错误: ${error}`);
          
          if (progress) {
              // 计算进度百分比
              const total = progress.total || 1;
              const current = progress.current || 0;
              const percent = Math.min(90, Math.floor((current / total) * 90));
              console.log(`计算进度: ${current}/${total} = ${percent}%`);
              setProgress(percent);
              
              // 显示当前处理的内容
              if (progress.episode_title) {
                console.log(`处理进度: ${progress.episode_title}`);
              }
            } else {
              console.log('进度数据为空，保持当前进度');
            }
          
          if (status === 'completed') {
            console.log('任务完成，清除轮询');
            clearInterval(statusInterval);
            statusInterval = null;
            
            // 完成进度
            setProgress(100);
            console.log('设置进度为100%');
            
            // 重新加载分镜数据
            console.log('重新加载分镜数据');
            loadShots();
            
            // 显示成功消息
            const result = statusResponse.data.result;
            if (result && result.success) {
              console.log(`分镜生成成功！共生成 ${result.count} 个镜头`);
              message.success(`分镜生成成功！共生成 ${result.count} 个镜头`);
            } else {
              console.error('分镜生成失败:', result?.error);
              message.error(result?.error || '分镜生成失败');
            }
            
            setIsGenerating(false);
            console.log('设置isGenerating为false');
          } else if (status === 'failed') {
            console.log('任务失败，清除轮询');
            clearInterval(statusInterval);
            statusInterval = null;
            
            // 显示失败消息
            console.error('分镜生成失败:', error);
            message.error(error || '分镜生成失败');
            setIsGenerating(false);
            console.log('设置isGenerating为false');
          } else if (status === 'not_found') {
            console.error('任务不存在或已过期');
            clearInterval(statusInterval);
            statusInterval = null;
            message.error('任务不存在或已过期');
            setIsGenerating(false);
            console.log('设置isGenerating为false');
          }
        } catch (error) {
          console.error('获取任务状态失败:', error);
          console.error('错误详情:', error.response?.data);
        }
      }, 2000); // 每2秒轮询一次
    } catch (error) {
      console.error('启动分镜生成任务失败:', error);
      console.error('错误详情:', error.response?.data);
      message.error('分镜生成失败: ' + (error.response?.data?.error || error.message));
      
      // 清除定时器
      if (statusInterval) {
        clearInterval(statusInterval);
      }
      setIsGenerating(false);
      console.log('设置isGenerating为false');
    }
  };

  // 显示视频生成确认对话框
  const handleGenerateVideo = async (shot) => {
    setCurrentShot(shot);
    
    // 加载资产列表
    await loadAssets();
    
    // 生成分镜的提示词
    let videoPrompt = "请根据参考图的风格，按照如下脚本，生成画面流畅、动作连贯、剧情合理的短视频。注意：画面中不要包含任何文字、字幕、标题或说明文字，只生成纯视觉画面：\n";
    
    // 分镜内容
    let shotContent = '';
    
    // 景别
    if (shot.camera) {
      shotContent += `${shot.camera}，`;
    }
    
    // 角色和动作
    if (shot.characters) {
      shotContent += `${shot.characters} `;
    }
    if (shot.action) {
      shotContent += `${shot.action}`;
    }
    
    // 台词
    if (shot.dialog) {
      shotContent += `，说：${shot.dialog}`;
    }
    
    // 音效
    if (shot.sound) {
      shotContent += `，音效：${shot.sound}`;
    }
    
    // 运镜
    if (shot.movement) {
      shotContent += `，运镜：${shot.movement}`;
    }
    
    videoPrompt += shotContent + "\n";
    
    // 视频风格 - 从项目设置中获取
    let videoStyle = '真人写实';
    if (project?.style) {
      videoStyle = project.style;
      // 解析风格值，确保显示中英文
      if (videoStyle.includes('|')) {
        const styleParts = videoStyle.split('|');
        videoStyle = `${styleParts[0].trim()} | ${styleParts[1].trim()}`;
      }
    }
    videoPrompt += `画风：【${videoStyle}】`;
    
    // 使用项目的视频设置
    const aspectRatio = project?.video_ratio || '16:9';
    const resolution = project?.video_resolution || '720P';
    const duration = shot.duration || 3;
    
    setVideoConfig({
      prompt: videoPrompt,
      aspectRatio: aspectRatio,
      duration: String(duration),
      resolution: resolution,
      referenceImage: null,
      referenceAssetId: null,
      referenceSource: 'none'
    });
    
    setVideoConfirmVisible(true);
  };
  
  // 开始生成视频
  const startGenerateVideo = async () => {
    console.log(`[startGenerateVideo] 开始生成视频, currentShot:`, currentShot);
    console.log(`[startGenerateVideo] currentShot.id=${currentShot?.id}, currentShot.index=${currentShot?.index}`);
    if (!currentShot) {
      message.error('未选择分镜');
      return;
    }

    if (!videoConfig.prompt) {
      message.error('提示词不能为空');
      return;
    }

    try {
      message.loading('正在提交视频生成任务...', 0);

      // 准备请求数据
      const requestData = {
        shot_id: currentShot.id,
        project_id: parseInt(id),
        prompt: videoConfig.prompt,
        ratio: videoConfig.aspectRatio,
        duration: parseInt(videoConfig.duration) || 5,
        resolution: videoConfig.resolution
      };

      // 添加视频提供商（如果指定）
      if (videoConfig.videoProvider) {
        requestData.video_provider = videoConfig.videoProvider;
      }

      // 使用分镜图（融图）作为参考图
      if (currentShot?.storyboard_image_path) {
        requestData.reference_image_url = currentShot.storyboard_image_path;
      }

      // 添加 MiniMax 专用参考素材
      if (videoConfig.referenceVideoUrl) {
        requestData.reference_video_url = videoConfig.referenceVideoUrl;
      }
      if (videoConfig.referenceAudioUrl) {
        requestData.reference_audio_url = videoConfig.referenceAudioUrl;
      }

      console.log(`[startGenerateVideo] 请求数据:`, requestData);

      // 调用API创建视频生成任务（使用Grok图生视频）
      console.log(`[startGenerateVideo] 调用API创建任务...`);
      const response = await aiApi.createVideoGeneration(requestData);
      console.log(`[startGenerateVideo] API响应:`, response.data);

      message.destroy();

      if (response.data.success) {
        message.success('视频生成任务已提交');
        const taskId = response.data.task_id;
        console.log(`[startGenerateVideo] 任务创建成功, taskId=${taskId}, currentShot.index=${currentShot.index}`);

        // 保存任务ID到分镜 - 使用函数式更新避免闭包问题
        console.log(`[startGenerateVideo] 准备更新shots状态，currentShot.id=${currentShot.id}, currentShot.index=${currentShot.index}`);
        setShots(prevShots => {
          console.log(`[startGenerateVideo] setShots回调，当前shots数量:`, prevShots.length);
          const updated = prevShots.map(shot => {
            // 优先使用id匹配，如果没有id则使用index匹配
            const match = shot.id ? shot.id === currentShot.id : shot.index === currentShot.index;
            console.log(`[startGenerateVideo] 检查shot: id=${shot.id}, index=${shot.index}, match=${match}`);
            if (match) {
              console.log(`[startGenerateVideo] 匹配成功，更新shot ${shot.index} 的video_status为generating`);
              return {
                ...shot,
                video_task_id: taskId,
                video_status: 'generating'
              };
            }
            return shot;
          });
          console.log(`[startGenerateVideo] 更新shots状态完成`);
          return updated;
        });

        // 开始轮询任务状态 - 传递分镜index而不是id
        console.log(`[startGenerateVideo] 调用startPollingVideoStatus: taskId=${taskId}, shotIndex=${currentShot.index}`);
        console.log(`[startGenerateVideo] startPollingVideoStatus函数类型:`, typeof startPollingVideoStatus);
        startPollingVideoStatus(taskId, currentShot.index);

        setVideoConfirmVisible(false);
      } else {
        if (response.data.setup_required) {
          Modal.error({
            title: '需要配置',
            content: (
              <div>
                <p>{response.data.error}</p>
                <p>请前往「项目设置」页面配置大模型API密钥。</p>
              </div>
            ),
            okText: '去配置',
            onOk: () => navigate(`/project/${id}/settings`)
          });
        } else {
          message.error(response.data.error || '提交失败');
        }
      }
    } catch (error) {
      message.destroy();
      console.error('视频生成失败:', error);
      message.error('视频生成失败: ' + (error.response?.data?.error || error.message));
    }
  };

  // 轮询视频生成状态
  const startPollingVideoStatus = useCallback((taskId, shotIndex) => {
    console.log(`[startPollingVideoStatus] 被调用: taskId=${taskId}, shotIndex=${shotIndex}`);
    
    // 初始化全局轮询管理器
    if (!window.videoPollIntervals) {
      window.videoPollIntervals = {};
    }
    
    // 检查是否已经在轮询此任务
    if (window.videoPollIntervals[taskId]) {
      console.log(`[startPollingVideoStatus] 任务已在轮询中: ${taskId}`);
      return;
    }
    
    console.log(`[startPollingVideoStatus] 开始轮询任务: ${taskId}, shotIndex: ${shotIndex}`);
    
    const pollInterval = setInterval(async () => {
      try {
        console.log(`[startPollingVideoStatus] 查询任务状态: ${taskId}`);
        const response = await aiApi.getVideoGenerationStatus(taskId);
        console.log(`[startPollingVideoStatus] 任务状态响应:`, response.data);

        if (response.data.success) {
          const status = response.data.status;
          console.log(`[startPollingVideoStatus] 任务状态: ${status}`);

          if (status === 'completed' || status === 'succeeded') {
            console.log(`[startPollingVideoStatus] 任务成功，准备清理轮询`);
            clearInterval(pollInterval);
            delete window.videoPollIntervals[taskId];

            console.log(`[startPollingVideoStatus] 更新分镜状态: shotIndex=${shotIndex}`);
            
            const videoUrl = response.data.video_urls && response.data.video_urls.length > 0 
              ? response.data.video_urls[0] 
              : null;
            
            const localVideoPath = response.data.video_path || videoUrl || null;
            
            setShots(prevShots => {
              return prevShots.map(shot => {
                if (shot.index === shotIndex) {
                  return {
                    ...shot,
                    video_status: 'completed',
                    video_path: localVideoPath || shot.video_path,
                    video_urls: response.data.video_urls
                  };
                }
                return shot;
              });
            });

            if (videoUrl) {
              message.success('视频生成成功！', 3);
            } else {
              message.success('视频生成成功，但未获取到视频URL', 3);
            }
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            delete window.videoPollIntervals[taskId];
            message.error('视频生成失败: ' + response.data.error);

            setShots(prevShots => prevShots.map(shot => {
              if (shot.index === shotIndex) {
                return {
                  ...shot,
                  video_status: 'failed',
                  video_error: response.data.error
                };
              }
              return shot;
            }));
          } else if (status === 'cancelled') {
            clearInterval(pollInterval);
            delete window.videoPollIntervals[taskId];
            message.warning('视频生成任务被取消');

            setShots(prevShots => prevShots.map(shot => {
              if (shot.index === shotIndex) {
                return {
                  ...shot,
                  video_status: 'cancelled'
                };
              }
              return shot;
            }));
          }
          // 其他状态继续轮询
        }
      } catch (error) {
        console.error('查询任务状态失败:', error);
      }
    }, 5000); // 每5秒查询一次

    // 保存轮询interval以便清理
    window.videoPollIntervals = window.videoPollIntervals || {};
    window.videoPollIntervals[taskId] = pollInterval;
    console.log(`[startPollingVideoStatus] 开始轮询任务: ${taskId}, shotIndex: ${shotIndex}`);
  }, [id, loadShots]);

  // 将函数赋值给 ref，供 loadShots 使用
  startPollingVideoStatusRef.current = startPollingVideoStatus;

  // 处理分镜图生成
  const handleGenerateStoryboardImage = async (record) => {
    setCurrentShot(record);
    const defaultPrompt = `根据以下分镜信息生成一张干净的场景图片，不要包含任何文字、字幕、标题或说明文字。场景：${record.scene}，角色：${record.characters}，动作：${record.action}，景别：${record.camera}，运镜：${record.movement}。要求：纯视觉画面，无任何文字元素，高清画质，电影感构图。`;
    
    await loadAssets();
    
    let referenceAssets = [];
    let referenceSource = 'none';
    
    if (record.characters && assets.length > 0) {
      const characterNames = record.characters.split(/[,，、]/).map(n => n.trim()).filter(n => n);
      const characterAssets = assets.filter(a => a.type === 'character' || a.asset_type === 'character');
      const matchedAssets = characterAssets.filter(c => {
        return characterNames.some(name => {
          const cleanName = name.replace(/^@/, '');
          return c.name.includes(cleanName) || cleanName.includes(c.name);
        });
      });
      
      if (matchedAssets.length > 0) {
        referenceAssets = matchedAssets.map(c => ({ 
          assetId: c.id, 
          asset: { ...c, type: c.type || 'character' }, 
          roleName: c.name 
        }));
        referenceSource = 'asset';
      }
    }
    
    const savedConfig = localStorage.getItem(`storyboard_config_${id}_${record.id}`);
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setStoryboardImageConfig({
          prompt: parsedConfig.prompt || defaultPrompt,
          aspectRatio: parsedConfig.aspectRatio || '16:9',
          referenceAssets: parsedConfig.referenceAssets || referenceAssets,
          referenceSource: parsedConfig.referenceSource || referenceSource
        });
      } catch (e) {
        setStoryboardImageConfig({
          prompt: defaultPrompt,
          aspectRatio: '16:9',
          referenceAssets,
          referenceSource
        });
      }
    } else {
      setStoryboardImageConfig({
        prompt: defaultPrompt,
        aspectRatio: '16:9',
        referenceAssets,
        referenceSource
      });
    }
    
    setStoryboardImageModalVisible(true);
  };

  const handleStoryboardImageClick = (record) => {
    setStoryboardImagePreview(record.storyboard_image_path);
  };

  const startGenerateStoryboardImage = async () => {
    if (!currentShot) {
      message.error('未选择分镜');
      return;
    }

    if (!storyboardImageConfig.prompt) {
      message.error('提示词不能为空');
      return;
    }

    try {
      message.loading('正在提交分镜图生成任务...', 0);

      let finalPrompt = storyboardImageConfig.prompt;
      const requestData = {
        shot_id: currentShot.id,
        project_id: parseInt(id),
        prompt: finalPrompt,
        ratio: storyboardImageConfig.aspectRatio,
        reference_assets: []
      };

      if (storyboardImageConfig.referenceSource === 'asset' && storyboardImageConfig.referenceAssets.length > 0) {
        const characterAssets = storyboardImageConfig.referenceAssets.filter(a => a.asset.type === 'character');
        const sceneAssets = storyboardImageConfig.referenceAssets.filter(a => a.asset.type === 'scene');

        let refDescription = '';
        if (characterAssets.length > 0) {
          refDescription += '，必须严格参考上传的角色图片，保持角色形象一致，角色的五官、发型、服装、身高比例完全与参考图一致，不得更改角色性别和外貌特征。';
          refDescription += `角色参考：${characterAssets.map(a => `${a.roleName || a.asset.name}@图${characterAssets.indexOf(a) + 1}`).join('、')}`;
        }
        if (sceneAssets.length > 0) {
          if (characterAssets.length > 0) refDescription += '；';
          refDescription += `场景参考：场景@图${characterAssets.length + 1}，场景风格和布局需与参考图一致。`;
        }
        finalPrompt += refDescription;
        requestData.prompt = finalPrompt;

        requestData.reference_assets = storyboardImageConfig.referenceAssets.map(a => a.asset.id);
      }

      const response = await aiApi.createStoryboardImage(requestData);
      message.destroy();

      if (response.data.success) {
        message.success('分镜图生成任务已提交');
        const taskId = response.data.task_id;

        setShots(prevShots => prevShots.map(shot => {
          if (shot.id === currentShot.id) {
            return {
              ...shot,
              storyboard_image_task_id: taskId,
              storyboard_image_status: 'generating'
            };
          }
          return shot;
        }));

        startPollingStoryboardImageStatus(taskId, currentShot.id);
        setStoryboardImageModalVisible(false);
      } else {
        message.error('分镜图生成失败: ' + (response.data.error || '未知错误'));
      }
    } catch (error) {
      message.destroy();
      message.error('分镜图生成失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const startPollingStoryboardImageStatus = useCallback((taskId, shotId) => {
    if (!window.storyboardImagePollIntervals) {
      window.storyboardImagePollIntervals = {};
    }

    if (window.storyboardImagePollIntervals[taskId]) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const response = await aiApi.getImageGenerationStatus(taskId, {
          shot_id: shotId,
          project_id: id
        });

        if (response.data.success) {
          const status = response.data.status;

          if (status === 'completed') {
            clearInterval(pollInterval);
            delete window.storyboardImagePollIntervals[taskId];
            message.success('分镜图生成成功！', 3);

            const localPath = response.data.local_path || 
              response.data.image_url || 
              (response.data.image_urls && response.data.image_urls.length > 0 ? response.data.image_urls[0] : null);

            if (localPath) {
              setShots(prevShots => prevShots.map(shot => {
                if (shot.id === shotId) {
                  return {
                    ...shot,
                    storyboard_image_path: localPath,
                    storyboard_image_status: 'completed',
                    storyboard_image_task_id: ''
                  };
                }
                return shot;
              }));
            }
          } else if (status === 'failed') {
            clearInterval(pollInterval);
            delete window.storyboardImagePollIntervals[taskId];
            message.error('分镜图生成失败: ' + response.data.error);

            setShots(prevShots => prevShots.map(shot => {
              if (shot.id === shotId) {
                return {
                  ...shot,
                  storyboard_image_status: 'failed'
                };
              }
              return shot;
            }));
          }
        }
      } catch (error) {
        console.error('查询分镜图任务状态失败:', error);
      }
    }, 5000);

    window.storyboardImagePollIntervals[taskId] = pollInterval;
  }, [id]);

  // 将函数赋值给 ref，供 loadShots 使用
  startPollingStoryboardImageStatusRef.current = startPollingStoryboardImageStatus;

  // 处理参考图来源选择
  const handleReferenceSourceChange = (e) => {
    const source = e.target.value;
    setVideoConfig({
      ...videoConfig,
      referenceSource: source,
      referenceAssetId: null,
      referenceImage: null
    });
  };

  // 处理资产选择
  const handleAssetSelect = (assetId) => {
    setVideoConfig({ ...videoConfig, referenceAssetId: assetId });
  };

  // 处理参考图上传
  const handleReferenceImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoConfig({ ...videoConfig, referenceImage: file });
    }
  };

  // 将文件转为Base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // 批量删除分镜
  const handleBatchDelete = () => {
    if (selectedShots.length === 0) {
      message.warning('请先选择要删除的分镜');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedShots.length} 个分镜吗？此操作不可恢复。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 过滤掉选中的分镜
          const remainingShots = shots.filter(shot => {
            const shotKey = `${shot.episode}-${shot.index}`;
            return !selectedShots.includes(shotKey);
          });
          // 按集数重新编号
          const episodeShotsMap = {};
          remainingShots.forEach(shot => {
            if (!episodeShotsMap[shot.episode]) {
              episodeShotsMap[shot.episode] = [];
            }
            episodeShotsMap[shot.episode].push(shot);
          });
          
          const renumberedShots = [];
          Object.keys(episodeShotsMap).forEach(episode => {
            episodeShotsMap[episode].forEach((shot, idx) => {
              renumberedShots.push({
                ...shot,
                index: idx + 1
              });
            });
          });
          
          // 保存到服务器
          await shotApi.save(id, renumberedShots);
          // 重新加载分镜数据，确保与服务器状态一致
          await loadShots();
          setSelectedShots([]);
          message.success(`成功删除 ${selectedShots.length} 个分镜`);
        } catch (error) {
          message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    });
  };

  // 处理单元格编辑
  const handleCellChange = (index, field, value) => {
    const newShots = shots.map(shot => {
      if (shot.index === index) {
        return { ...shot, [field]: value };
      }
      return shot;
    });
    setShots(newShots);
  };

  // 保存分镜
  const handleSaveShots = async () => {
    try {
      await shotApi.save(id, shots);
      message.success('分镜保存成功');
    } catch (error) {
      message.error('保存失败: ' + (error.response?.data?.error || error.message));
    }
  };

  // 生成集数序号（如：11-1, 12-2）
  const getEpisodeIndex = (episode, index) => {
    // 从集数中提取数字（如：第11集 -> 11，第二集 -> 2）
    let episodeNum = '0';
    if (episode && typeof episode === 'string') {
      // 中文数字映射
      const chineseNumbers = {
        '零': 0, '一': 1, '二': 2, '三': 3, '四': 4,
        '五': 5, '六': 6, '七': 7, '八': 8, '九': 9,
        '十': 10, '百': 100, '千': 1000
      };
      
      // 提取阿拉伯数字
      const arabicMatch = episode.match(/\d+/);
      if (arabicMatch) {
        episodeNum = arabicMatch[0];
      } else {
        // 提取中文数字
        const chineseMatch = episode.match(/第([一二三四五六七八九十百千]+)集/);
        if (chineseMatch && chineseMatch[1]) {
          const chineseNum = chineseMatch[1];
          // 简单处理中文数字转换
          if (chineseNumbers[chineseNum]) {
            episodeNum = chineseNumbers[chineseNum].toString();
          } else if (chineseNum === '十一') {
            episodeNum = '11';
          } else if (chineseNum === '十二') {
            episodeNum = '12';
          } else if (chineseNum === '十三') {
            episodeNum = '13';
          } else if (chineseNum === '十四') {
            episodeNum = '14';
          } else if (chineseNum === '十五') {
            episodeNum = '15';
          } else if (chineseNum === '十六') {
            episodeNum = '16';
          } else if (chineseNum === '十七') {
            episodeNum = '17';
          } else if (chineseNum === '十八') {
            episodeNum = '18';
          } else if (chineseNum === '十九') {
            episodeNum = '19';
          } else if (chineseNum === '二十') {
            episodeNum = '20';
          }
        }
      }
    }
    // 确保index是正数
    const displayIndex = Math.max(1, Number(index) || 1);
    return `${episodeNum}-${displayIndex}`;
  };

  // 为每一集的分镜重新编号
  const getRenumberedShots = () => {
    const episodeShotsMap = {};
    
    // 按集数分组
    shots.forEach(shot => {
      const episode = shot.episode || '未知集数';
      if (!episodeShotsMap[episode]) {
        episodeShotsMap[episode] = [];
      }
      episodeShotsMap[episode].push(shot);
    });
    
    // 对每一集的分镜重新编号
    const renumberedShots = [];
    Object.keys(episodeShotsMap).forEach(episode => {
      // 按原始index排序，确保顺序正确
      episodeShotsMap[episode].sort((a, b) => {
        const aIndex = Number(a.index) || 0;
        const bIndex = Number(b.index) || 0;
        return aIndex - bIndex;
      });
      
      episodeShotsMap[episode].forEach((shot, idx) => {
        renumberedShots.push({
          ...shot,
          displayIndex: idx + 1
        });
      });
    });
    
    return renumberedShots;
  };

  // 解析集数序号用于排序（如：11-1 -> [11, 1]）
  const parseEpisodeIndex = (episode, index) => {
    let episodeNum = 0;
    if (episode && typeof episode === 'string') {
      // 中文数字映射
      const chineseNumbers = {
        '零': 0, '一': 1, '二': 2, '三': 3, '四': 4,
        '五': 5, '六': 6, '七': 7, '八': 8, '九': 9,
        '十': 10, '百': 100, '千': 1000
      };
      
      // 提取阿拉伯数字
      const arabicMatch = episode.match(/\d+/);
      if (arabicMatch) {
        episodeNum = parseInt(arabicMatch[0]) || 0;
      } else {
        // 提取中文数字
        const chineseMatch = episode.match(/第([一二三四五六七八九十百千]+)集/);
        if (chineseMatch && chineseMatch[1]) {
          const chineseNum = chineseMatch[1];
          // 简单处理中文数字转换
          if (chineseNumbers[chineseNum]) {
            episodeNum = chineseNumbers[chineseNum];
          } else if (chineseNum === '十一') {
            episodeNum = 11;
          } else if (chineseNum === '十二') {
            episodeNum = 12;
          } else if (chineseNum === '十三') {
            episodeNum = 13;
          } else if (chineseNum === '十四') {
            episodeNum = 14;
          } else if (chineseNum === '十五') {
            episodeNum = 15;
          } else if (chineseNum === '十六') {
            episodeNum = 16;
          } else if (chineseNum === '十七') {
            episodeNum = 17;
          } else if (chineseNum === '十八') {
            episodeNum = 18;
          } else if (chineseNum === '十九') {
            episodeNum = 19;
          } else if (chineseNum === '二十') {
            episodeNum = 20;
          }
        }
      }
    }
    return [episodeNum, Number(index) || 0];
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'displayIndex',
      key: 'displayIndex',
      width: 80,
      render: (text, record) => getEpisodeIndex(record.episode, text || record.index)
    },
    { title: '场景', dataIndex: 'scene', key: 'scene', width: 120 },
    { title: '角色', dataIndex: 'characters', key: 'characters', width: 100 },
    {
      title: '动作',
      dataIndex: 'action',
      key: 'action',
      ellipsis: true,
      width: 200,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleCellChange(record.index, 'action', e.target.value)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    {
      title: '台词',
      dataIndex: 'dialog',
      key: 'dialog',
      ellipsis: true,
      width: 150,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleCellChange(record.index, 'dialog', e.target.value)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    {
      title: '字幕',
      dataIndex: 'subtitle',
      key: 'subtitle',
      ellipsis: true,
      width: 150,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleCellChange(record.index, 'subtitle', e.target.value)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    {
      title: '景别',
      dataIndex: 'camera',
      key: 'camera',
      ellipsis: true,
      width: 100,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleCellChange(record.index, 'camera', e.target.value)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    {
      title: '运镜',
      dataIndex: 'movement',
      key: 'movement',
      width: 100,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleCellChange(record.index, 'movement', e.target.value)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    {
      title: '音效',
      dataIndex: 'sound',
      key: 'sound',
      width: 80,
      ellipsis: true,
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => handleCellChange(record.index, 'sound', e.target.value)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    { 
      title: '时长',
      dataIndex: 'duration',
      key: 'duration',
      width: 60,
      render: (text, record) => (
        <Input
          type="number"
          value={text}
          onChange={(e) => handleCellChange(record.index, 'duration', parseInt(e.target.value) || 3)}
          bordered={false}
          style={{ padding: 0 }}
        />
      )
    },
    { 
      title: '分镜图', 
      dataIndex: 'storyboard_image_path', 
      key: 'storyboard_image', 
      width: 120,
      render: (text, record) => {
        const imageStatus = record.storyboard_image_status;
        
        if (imageStatus === 'generating') {
          return (
            <div style={{ textAlign: 'center', padding: '5px 0' }}>
              <div style={{ 
                width: '80px', 
                height: '50px', 
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px'
              }}>
                <span style={{ fontSize: '12px', color: '#52c41a' }}>生成中...</span>
              </div>
            </div>
          );
        }
        
        if (text) {
          return (
            <div style={{ textAlign: 'center' }}>
              <img 
                src={getFullImageUrl(text)} 
                alt="分镜图" 
                style={{ width: '80px', height: '50px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                onClick={() => handleStoryboardImageClick(record)}
                onError={(e) => {
                  console.error('[StoryboardImage] 图片加载失败:', text);
                }}
              />
              <Button 
                size="small" 
                type="link" 
                onClick={() => handleGenerateStoryboardImage(record)}
                style={{ marginTop: 4, padding: 0, color: '#1890ff', fontSize: 12 }}
              >
                重新生成
              </Button>
            </div>
          );
        }
        
        return (
          <div style={{ textAlign: 'center' }}>
            <Button 
              size="small" 
              onClick={() => handleGenerateStoryboardImage(record)}
              style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', color: '#fff' }}
            >
              生成融图
            </Button>
          </div>
        );
      }
    },
    { 
      title: '视频', 
      dataIndex: 'video_path', 
      key: 'video', 
      width: 150,
      render: (text, record) => {
        const videoStatus = record.video_status;
        const shotId = record.id;
        console.log(`[renderVideoCell] shot ${record.index} (id=${shotId}): video_status=${videoStatus}, video_path=${text ? text.substring(0, 50) : '无'}...`);
        
        // 根据视频状态显示不同的UI
        if (videoStatus === 'generating') {
          return (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ 
                width: '80px', 
                height: '50px', 
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px'
              }}>
                <span style={{ fontSize: '12px', color: '#52c41a' }}>生成中...</span>
              </div>
              <Button 
                size="small" 
                type="link" 
                onClick={() => {
                  setShots(shots.map(s => {
                    if (s.id === shotId) {
                      return { ...s, video_status: 'pending', video_task_id: '' };
                    }
                    return s;
                  }));
                  message.info('已取消视频生成');
                }}
                style={{ marginTop: 4, padding: 0, color: '#ff4d4f', fontSize: 12 }}
              >
                取消
              </Button>
            </div>
          );
        }
        
        // pending 状态显示为未生成
        if (videoStatus === 'pending' || !videoStatus) {
          return (
            <span style={{ color: '#999', fontSize: '12px' }}>未生成</span>
          );
        }
        
        if (videoStatus === 'failed') {
          return (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ 
                width: '80px', 
                height: '50px', 
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              title={record.video_error || '生成失败'}
              onClick={() => message.error(record.video_error || '视频生成失败')}
              >
                <span style={{ fontSize: '12px', color: '#ff4d4f' }}>生成失败</span>
              </div>
            </div>
          );
        }
        
        if (text) {
          return (
            <div style={{ textAlign: 'center' }}>
              {/* 显示视频缩略图 */}
              <div 
                style={{ 
                  width: '80px', 
                  height: '50px', 
                  cursor: 'pointer', 
                  borderRadius: '4px',
                  overflow: 'hidden',
                  margin: '0 auto',
                  border: '1px solid #d9d9d9'
                }} 
                onClick={() => handleVideoPreview(record)}
              >
                {/* 尝试加载缩略图，如果不存在则显示默认播放图标 */}
                <img 
                  src={getFullVideoUrl(text).replace('.mp4', '.jpg')} 
                  alt="视频缩略图" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    // 当缩略图加载失败时，显示播放图标
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    backgroundColor: '#f0f0f0',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PlayCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                </div>
              </div>
              <div style={{ marginTop: 4, fontSize: '12px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '80px', margin: '0 auto' }}>
                {text.split('/').pop()}
              </div>
            </div>
          );
        }
        
        return (
          <span style={{ color: '#999', fontSize: '12px' }}>未生成</span>
        );
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<VideoCameraOutlined />}
            onClick={() => handleGenerateVideo(record)}
          >
            生成
          </Button>
          <Button
            type="link"
            icon={<UploadOutlined />}
            onClick={() => handleUploadVideo(record)}
          >
            上传
          </Button>
          {record.video_path && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteVideo(record)}
            >
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // 过滤并排序分镜
  const renumberedShots = getRenumberedShots();

  const filteredShots = activeEpisode === 'all'
    ? [...renumberedShots].sort((a, b) => {
        const [aEpisode, aIndex] = parseEpisodeIndex(a.episode, a.displayIndex);
        const [bEpisode, bIndex] = parseEpisodeIndex(b.episode, b.displayIndex);
        if (aEpisode !== bEpisode) {
          return aEpisode - bEpisode;
        }
        return aIndex - bIndex;
      })
    : renumberedShots.filter(s => s.episode === activeEpisode);

  return (
    <div>
      {/* 返回按钮 */}
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/project/${id}`)}
        style={{ marginBottom: 16 }}
      >
        返回项目管理
      </Button>

      {project?.order && (
        <Card
          style={{ 
            background: '#1a1a24', 
            border: '1px solid #2a2a3a', 
            marginBottom: 24,
            backgroundImage: 'linear-gradient(135deg, rgba(0, 212, 170, 0.1) 0%, rgba(0, 212, 170, 0.05) 100%)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h2 style={{ color: '#e8e8ec', marginBottom: 8, fontSize: 20 }}>{project.order.title}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#a0a0b0' }}>
                <span style={{ fontWeight: 'bold', color: '#00d4aa' }}>{project.order.style}</span>
                <span>|</span>
                <span>{project.order.video_ratio}</span>
                <span>|</span>
                <span>{project.video_resolution}</span>
              </div>
              {project.order.description && (
                <p style={{ color: '#a0a0b0', marginTop: 8, fontSize: 14 }}>{project.order.description}</p>
              )}
            </div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#a0a0b0', marginBottom: 4 }}>发布者</div>
                <div style={{ fontSize: 14, color: '#e8e8ec', fontWeight: 'bold' }}>
                  {project.order.company_user?.nickname || '未知'}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#a0a0b0', marginBottom: 4 }}>总价</div>
                <div style={{ fontSize: 18, color: '#ffd700', fontWeight: 'bold' }}>
                  {project.order.total_price} 积分
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#a0a0b0', marginBottom: 4 }}>时长</div>
                <div style={{ fontSize: 14, color: '#e8e8ec', fontWeight: 'bold' }}>
                  {project.order.duration_minutes} 分钟
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#a0a0b0', marginBottom: 4 }}>交付周期</div>
                <div style={{ fontSize: 14, color: '#e8e8ec', fontWeight: 'bold' }}>
                  {project.order.delivery_days} 天
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#a0a0b0', marginBottom: 4 }}>预期交付</div>
                <div style={{ fontSize: 14, color: project.order.expected_delivery_date ? '#00d4aa' : '#a0a0b0', fontWeight: 'bold' }}>
                  {project.order.expected_delivery_date ? new Date(project.order.expected_delivery_date).toLocaleDateString() : '-'}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card
        title={
          <span>
            <span style={{ marginRight: 8 }}>分镜生成</span>
          </span>
        }
        extra={
          <Space>
            <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleGenerateStoryboard} loading={isGenerating}>
              生成分镜
            </Button>
            {/* 导入导出分镜按钮 */}
            <Button
              icon={<ImportOutlined />}
              onClick={() => setIsImportModalVisible(true)}
            >
              导入分镜
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={() => setIsExportModalVisible(true)}
              disabled={shots.length === 0}
            >
              导出分镜
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleSaveShots}
              disabled={shots.length === 0}
            >
              保存分镜
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleBatchDelete} disabled={selectedShots.length === 0}>
              批量删除{selectedShots.length > 0 ? ` (${selectedShots.length})` : ''}
            </Button>
          </Space>
        }
      >
        {isGenerating && (
          <div style={{ marginBottom: 16 }}>
            <Progress percent={progress} status="active" />
            <p style={{ color: '#1890ff', fontWeight: 'bold' }}>正在生成分镜，请稍候...</p>
          </div>
        )}

        <Tabs activeKey={activeEpisode} onChange={setActiveEpisode}>
          <TabPane tab="全部" key="all">
            <Table
              dataSource={filteredShots}
              columns={columns}
              rowKey={(record) => `${record.episode}-${record.index}`}
              pagination={{ pageSize: 20 }}
              scroll={{ x: 1200 }}
              rowSelection={{
                selectedRowKeys: selectedShots,
                onChange: (selectedRowKeys) => setSelectedShots(selectedRowKeys),
              }}
            />
          </TabPane>
          {episodes.map(episode => (
            <TabPane tab={episode} key={episode}>
              <Table
                dataSource={filteredShots}
                columns={columns}
                rowKey={(record) => `${record.episode}-${record.index}`}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 1200 }}
              rowSelection={{
                selectedRowKeys: selectedShots,
                onChange: (selectedRowKeys) => setSelectedShots(selectedRowKeys),
              }}
              />
            </TabPane>
          ))}
        </Tabs>
      </Card>

      <Card title="分镜统计" style={{ marginTop: 16 }}>
        <p>总分镜数: {shots.length}</p>
        <p>总集数: {episodes.length}</p>
        <p>已生成视频: {shots.filter(s => s.video_path).length}</p>
      </Card>

      {/* 导入分镜模态框 */}
      <Modal
        title="导入分镜"
        visible={isImportModalVisible}
        onCancel={() => {
          setIsImportModalVisible(false);
          setImportFile(null);
        }}
        footer={null}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 16 }}>
              支持导入 JSON 格式或 Excel 格式的分镜文件。
            </p>
            <p style={{ color: '#999', fontSize: 12 }}>
              JSON 格式示例：<br/>
              {`[{ "index": 1, "episode": "第一集", "scene": "场景描述", "characters": "角色", "action": "动作", "dialog": "对话", "camera": "镜头", "duration": 3 }]`}
            </p>
          </div>
          
          <Upload
            accept=".json,.xlsx,.xls"
            beforeUpload={(file) => {
              setImportFile(file);
              return false;
            }}
            onRemove={() => setImportFile(null)}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
          
          {importFile && (
            <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 4 }}>
              <span style={{ color: '#52c41a' }}>已选择: {importFile.name}</span>
            </div>
          )}
          
          <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button onClick={() => {
              setIsImportModalVisible(false);
              setImportFile(null);
            }}>
              取消
            </Button>
            <Button 
              type="primary" 
              icon={<ImportOutlined />}
              disabled={!importFile}
              onClick={async () => {
                if (!importFile) {
                  message.warning('请先选择文件');
                  return;
                }
                
                try {
                  const fileExtension = importFile.name.split('.').pop().toLowerCase();
                  let shotsData = [];
                  
                  if (fileExtension === 'json') {
                    const content = await importFile.text();
                    shotsData = JSON.parse(content);
                  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                    const XLSX = await import('xlsx');
                    const data = await importFile.arrayBuffer();
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    shotsData = XLSX.utils.sheet_to_json(firstSheet);
                  } else {
                    message.error('不支持的文件格式，请上传 JSON 或 Excel 文件');
                    return;
                  }
                  
                  if (!Array.isArray(shotsData)) {
                    message.error('文件格式错误：数据必须是数组');
                    return;
                  }
                  
                  // 准备分镜数据
                  const shotsToSave = shotsData.map(shotData => ({
                    index: shotData.index || shotData['序号'] || shotData['index'] || 0,
                    episode: shotData.episode || shotData['集数'] || shotData['episode'] || '',
                    scene: shotData.scene || shotData['场景'] || shotData['scene'] || '',
                    characters: shotData.characters || shotData['角色'] || shotData['characters'] || '',
                    action: shotData.action || shotData['动作'] || shotData['action'] || '',
                    dialog: shotData.dialog || shotData['对话'] || shotData['dialog'] || '',
                    subtitle: shotData.subtitle || shotData['字幕'] || shotData['subtitle'] || '',
                    camera: shotData.camera || shotData['景别'] || shotData['镜头'] || shotData['camera'] || '',
                    movement: shotData.movement || shotData['运镜'] || shotData['movement'] || '',
                    sound: shotData.sound || shotData['音效'] || shotData['sound'] || '',
                    duration: shotData.duration || shotData['时长'] || shotData['duration'] || 3,
                    reference: shotData.reference || shotData['参考'] || shotData['reference'] || '',
                    prompt: shotData.prompt || shotData['提示词'] || shotData['prompt'] || ''
                  }));
                  
                  // 批量保存分镜
                  await shotApi.save(id, shotsToSave);
                  
                  message.success(`成功导入 ${shotsToSave.length} 个分镜`);
                  setIsImportModalVisible(false);
                  setImportFile(null);
                  loadShots();
                } catch (error) {
                  message.error('导入失败: ' + error.message);
                }
              }}
            >
              开始导入
            </Button>
          </Space>
        </div>
      </Modal>

      {/* 导出分镜模态框 */}
      <Modal
        title="导出分镜"
        visible={isExportModalVisible}
        onCancel={() => setIsExportModalVisible(false)}
        footer={null}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ color: '#666', marginBottom: 24 }}>
            当前共有 <strong>{shots.length}</strong> 个分镜，请选择导出格式：
          </p>
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button 
              icon={<FileTextOutlined />} 
              size="large"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => {
                // 根据当前选中的集数过滤导出数据
                const exportShots = activeEpisode === 'all' ? shots : shots.filter(s => s.episode === activeEpisode);
                
                const exportData = exportShots.map(shot => ({
                  index: shot.displayIndex || shot.index,
                  episode: shot.episode,
                  scene: shot.scene,
                  characters: shot.characters,
                  action: shot.action,
                  dialog: shot.dialog,
                  subtitle: shot.subtitle,
                  camera: shot.camera,
                  movement: shot.movement,
                  sound: shot.sound,
                  duration: shot.duration,
                  reference: shot.reference,
                  prompt: shot.prompt,
                  group_id: shot.group_id,
                  video_status: shot.video_status
                }));
                
                const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `shots_${id}_${activeEpisode === 'all' ? 'all' : activeEpisode.replace(/[^0-9]/g, '')}_${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                message.success('JSON 格式导出成功');
                setIsExportModalVisible(false);
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>导出为 JSON 格式</div>
                <div style={{ fontSize: 12, color: '#999' }}>适合程序处理，包含完整数据</div>
              </div>
            </Button>
            
            <Button 
              icon={<FileExcelOutlined />} 
              size="large"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={async () => {
                try {
                  const XLSX = await import('xlsx');
                  
                  // 根据当前选中的集数过滤导出数据
                  const exportShots = activeEpisode === 'all' ? shots : shots.filter(s => s.episode === activeEpisode);
                  
                  const exportData = exportShots.map(shot => ({
                    '序号': shot.displayIndex || shot.index,
                    '集数': shot.episode,
                    '场景': shot.scene,
                    '角色': shot.characters,
                    '动作': shot.action,
                    '对话': shot.dialog,
                    '字幕': shot.subtitle,
                    '景别': shot.camera,
                    '运镜': shot.movement,
                    '音效': shot.sound,
                    '时长': shot.duration,
                    '参考': shot.reference,
                    '提示词': shot.prompt,
                    '分镜组': shot.group_id,
                    '视频状态': shot.video_status
                  }));
                  
                  const worksheet = XLSX.utils.json_to_sheet(exportData);
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, '分镜列表');
                  
                  XLSX.writeFile(workbook, `shots_${id}_${activeEpisode === 'all' ? 'all' : activeEpisode.replace(/[^0-9]/g, '')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
                  
                  message.success('Excel 格式导出成功');
                  setIsExportModalVisible(false);
                } catch (error) {
                  message.error('导出 Excel 失败: ' + error.message);
                }
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>导出为 Excel 格式</div>
                <div style={{ fontSize: 12, color: '#999' }}>适合人工查看和编辑</div>
              </div>
            </Button>
          </Space>
          
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Button onClick={() => setIsExportModalVisible(false)}>取消</Button>
          </div>
        </div>
      </Modal>

      {/* 视频生成确认对话框 */}
      <Modal
        title="生成视频配置"
        visible={videoConfirmVisible}
        onCancel={() => setVideoConfirmVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setVideoConfirmVisible(false)}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={startGenerateVideo}>
            开始生成
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: '10px 0' }}>
          {/* 提示词编辑 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>提示词</div>
            <textarea
              value={videoConfig.prompt}
              onChange={(e) => setVideoConfig({ ...videoConfig, prompt: e.target.value })}
              style={{
                width: '100%',
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                maxHeight: 200,
                height: 200,
                overflow: 'auto',
                lineHeight: 1.6,
                border: '1px solid #d9d9d9',
                resize: 'none'
              }}
            />
          </div>

          {/* 画面比例 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>画面比例</div>
            <Select
              value={videoConfig.aspectRatio}
              style={{ width: 200 }}
              disabled
            >
              <Option value="16:9">16:9 (横屏)</Option>
              <Option value="9:16">9:16 (竖屏)</Option>
              <Option value="1:1">1:1 (方形)</Option>
              <Option value="4:3">4:3</Option>
            </Select>
          </div>

          {/* 视频时长 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>视频时长</div>
            <Input
              type="number"
              value={videoConfig.duration}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 5;
                const minDuration = videoConfig.videoProvider === 'MiniMax' ? 4 : 5;
                const maxDuration = videoConfig.videoProvider === 'MiniMax' ? 15 : 60;
                setVideoConfig({ ...videoConfig, duration: Math.max(minDuration, Math.min(maxDuration, value)).toString() });
              }}
              style={{ width: 200 }}
              suffix="秒"
              min={videoConfig.videoProvider === 'MiniMax' ? 4 : 5}
              max={videoConfig.videoProvider === 'MiniMax' ? 15 : 60}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
              {videoConfig.videoProvider === 'MiniMax' ? 'MiniMax H3 支持 4-15 秒' : '支持 5-60 秒'}
            </div>
          </div>

          {/* 分辨率 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>分辨率</div>
            <Select
              value={videoConfig.resolution}
              style={{ width: 200 }}
              disabled
            >
              <Option value="720P">720P</Option>
              <Option value="1080P">1080P</Option>
              <Option value="4K">4K</Option>
            </Select>
          </div>

          {/* 视频提供商选择 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>视频提供商</div>
            <Select
              value={videoConfig.videoProvider || ''}
              onChange={(value) => setVideoConfig({ ...videoConfig, videoProvider: value })}
              style={{ width: 250 }}
              placeholder="使用系统默认设置"
              allowClear
            >
              <Option value="">使用系统默认设置</Option>
              <Option value="MiniMax">MiniMax H3 (参考生视频)</Option>
              <Option value="火山引擎">火山引擎 Seedance</Option>
              <Option value="Grok">Grok</Option>
              <Option value="时识科技">时识科技</Option>
            </Select>
            <div style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
              选择后将使用对应的视频生成服务，需先在后台配置好API密钥
            </div>
          </div>

          {/* MiniMax 专用参考素材 */}
          {videoConfig.videoProvider === 'MiniMax' && (
            <>
              <div style={{ marginBottom: 15, padding: '10px', background: '#e6f7ff', borderRadius: 4, fontSize: 12 }}>
                💡 MiniMax H3 支持参考视频和参考音频，可用于视频风格迁移和音色参考
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>
                  参考视频 URL <span style={{ color: '#999', fontWeight: 'normal', fontSize: 12 }}>(可选，用于视频风格/动作参考)</span>
                </div>
                <Input
                  value={videoConfig.referenceVideoUrl}
                  onChange={(e) => setVideoConfig({ ...videoConfig, referenceVideoUrl: e.target.value })}
                  placeholder="https://example.com/reference-video.mp4"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>
                  参考音频 URL <span style={{ color: '#999', fontWeight: 'normal', fontSize: 12 }}>(可选，用于音色参考)</span>
                </div>
                <Input
                  value={videoConfig.referenceAudioUrl}
                  onChange={(e) => setVideoConfig({ ...videoConfig, referenceAudioUrl: e.target.value })}
                  placeholder="https://example.com/reference-audio.mp3"
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}

          {/* 分镜图（融图）预览 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>分镜图（融图）</div>
            {currentShot?.storyboard_image_path ? (
              <div>
                <img 
                  src={getFullImageUrl(currentShot.storyboard_image_path)} 
                  alt="分镜图" 
                  style={{ width: 200, height: 110, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>
                  已加载分镜图，将作为视频生成参考
                </div>
              </div>
            ) : (
              <div style={{ padding: '40px', border: '1px dashed #d9d9d9', borderRadius: 4, textAlign: 'center' }}>
                <div style={{ color: '#999', marginBottom: 8 }}>暂无分镜图</div>
                <div style={{ fontSize: 12, color: '#999' }}>请先生成分镜图（融图），再生成视频</div>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* 分镜图生成配置弹窗 */}
      <Modal
        title="生成分镜图（融图）"
        visible={storyboardImageModalVisible}
        onCancel={() => setStoryboardImageModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setStoryboardImageModalVisible(false)}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={startGenerateStoryboardImage}>
            开始生成
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: '10px 0' }}>
          {/* 提示词编辑 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>提示词</div>
            <textarea
              value={storyboardImageConfig.prompt}
              onChange={(e) => saveStoryboardConfig({ ...storyboardImageConfig, prompt: e.target.value })}
              style={{
                width: '100%',
                background: '#f5f5f5',
                padding: 12,
                borderRadius: 4,
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                maxHeight: 200,
                height: 200,
                overflow: 'auto',
                lineHeight: 1.6,
                border: '1px solid #d9d9d9',
                resize: 'none'
              }}
            />
          </div>

          {/* 画面比例 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>画面比例</div>
            <Select
              value={storyboardImageConfig.aspectRatio}
              onChange={(value) => saveStoryboardConfig({ ...storyboardImageConfig, aspectRatio: value })}
              style={{ width: 200 }}
            >
              <Option value="16:9">16:9 (横屏)</Option>
              <Option value="9:16">9:16 (竖屏)</Option>
              <Option value="1:1">1:1 (方形)</Option>
              <Option value="4:3">4:3</Option>
            </Select>
          </div>

          {/* 参考图选择 */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>参考图（可选，支持多选）</div>
              <Radio.Group 
                value={storyboardImageConfig.referenceSource} 
              onChange={(e) => saveStoryboardConfig({ ...storyboardImageConfig, referenceSource: e.target.value, referenceAssets: [] })}
              style={{ marginBottom: 12 }}
              >
                <Radio value="none">不使用参考图</Radio>
                <Radio value="asset">从资产库选择</Radio>
              </Radio.Group>
              
              {storyboardImageConfig.referenceSource === 'asset' && (
                <div style={{ marginTop: 12 }}>
                  {assetsLoading ? (
                    <div style={{ color: '#999' }}>加载资产中...</div>
                  ) : assets.length === 0 ? (
                    <div style={{ color: '#999' }}>资产库为空，请先添加资产</div>
                  ) : (
                    <>
                      <div style={{ 
                        maxHeight: 200, 
                        overflowY: 'auto', 
                        border: '1px solid #d9d9d9', 
                        borderRadius: 4,
                        padding: 8 
                      }}>
                        {assets.map(asset => {
                          const isSelected = storyboardImageConfig.referenceAssets.some(a => a.asset.id === asset.id);
                          const selectedIndex = storyboardImageConfig.referenceAssets.findIndex(a => a.asset.id === asset.id);
                          const selectedItem = storyboardImageConfig.referenceAssets[selectedIndex];
                          
                          return (
                            <div
                              key={asset.id}
                              onClick={() => {
                                if (isSelected) {
                                  saveStoryboardConfig({
                                    ...storyboardImageConfig,
                                    referenceAssets: storyboardImageConfig.referenceAssets.filter(a => a.asset.id !== asset.id)
                                  });
                                } else {
                                  saveStoryboardConfig({
                                    ...storyboardImageConfig,
                                    referenceAssets: [...storyboardImageConfig.referenceAssets, { assetId: asset.id, asset, roleName: '' }]
                                  });
                                }
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                marginBottom: 4,
                                background: isSelected ? '#e6f7ff' : 'transparent',
                                border: isSelected ? '1px solid #1890ff' : '1px solid transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                              }}
                            >
                              <div style={{ 
                                width: 20, 
                                height: 20, 
                                borderRadius: 4, 
                                border: '1px solid #d9d9d9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 'bold'
                              }}>
                                {isSelected ? selectedIndex + 1 : ''}
                              </div>
                              {asset.image_path && (
                                <img 
                                  src={asset.image_path} 
                                  alt={asset.name}
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                />
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold' }}>{asset.name}</div>
                                <div style={{ fontSize: 12, color: '#999' }}>
                                  {asset.type === 'character' ? '角色' : 
                                   asset.type === 'prop' ? '道具' : 
                                   asset.type === 'scene' ? '场景' : asset.type}
                                </div>
                              </div>
                              {asset.type === 'character' && isSelected && (
                                <Input
                                  placeholder="角色名"
                                  value={selectedItem?.roleName || ''}
                                  onChange={(e) => {
                                    const newAssets = [...storyboardImageConfig.referenceAssets];
                                    newAssets[selectedIndex] = { ...newAssets[selectedIndex], roleName: e.target.value };
                                    saveStoryboardConfig({ ...storyboardImageConfig, referenceAssets: newAssets });
                                  }}
                                  style={{ width: 80, fontSize: 12 }}
                                  size="small"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {storyboardImageConfig.referenceAssets.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ color: '#52c41a', marginBottom: 4 }}>
                            已选择 {storyboardImageConfig.referenceAssets.length} 个资产：
                          </div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {storyboardImageConfig.referenceAssets.map((item, idx) => (
                              <span key={idx} style={{ marginRight: 12 }}>
                                <span style={{ color: '#1890ff' }}>图{idx + 1}</span>：
                                {item.asset.type === 'character' ? (
                                  <span>{item.roleName || item.asset.name}（角色）</span>
                                ) : item.asset.type === 'scene' ? (
                                  <span>{item.asset.name}（场景）</span>
                                ) : (
                                  <span>{item.asset.name}</span>
                                )}
                              </span>
                            ))}
                          </div>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                            提示：为角色资产输入角色名后，系统会在提示词中添加"角色名@图N"的标注，帮助AI识别对应角色
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
        </div>
      </Modal>

      {/* 分镜图预览弹窗 */}
      <Modal
        title="分镜图预览"
        open={!!storyboardImagePreview}
        onCancel={() => setStoryboardImagePreview(null)}
        footer={null}
        width={600}
      >
        <div style={{ textAlign: 'center' }}>
          <img 
            src={getFullImageUrl(storyboardImagePreview)} 
            alt="分镜图预览" 
            style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain', borderRadius: 8 }}
          />
        </div>
      </Modal>

      {/* 视频预览模态框 */}
      <Modal
        title="视频预览"
        open={isVideoPreviewVisible}
        onCancel={handleCancelVideoPreview}
        footer={null}
        width={800}
      >
        {previewVideo && (
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ marginBottom: 16 }}>第{previewVideo.index}镜 - {previewVideo.episode}</h4>
            <div style={{ marginBottom: 16 }}>
              <video 
                src={getFullVideoUrl(previewVideo.video_path)} 
                controls 
                style={{ width: '100%', maxHeight: '500px' }}
                onError={(e) => {
                  console.error('[VideoPreview] 视频加载失败:', e);
                  console.error('[VideoPreview] 视频路径:', previewVideo.video_path);
                  console.error('[VideoPreview] 完整URL:', getFullVideoUrl(previewVideo.video_path));
                  message.error('视频加载失败，请检查网络连接或刷新页面重试');
                }}
                onLoadedData={() => {
                  console.log('[VideoPreview] 视频加载成功');
                }}
                onLoadStart={() => {
                  console.log('[VideoPreview] 开始加载视频:', getFullVideoUrl(previewVideo.video_path));
                }}
              />
            </div>
            {/* 如果视频是外部URL，显示在新窗口打开的链接 */}
            {previewVideo.video_path && previewVideo.video_path.startsWith('http') && (
              <div style={{ marginBottom: 16 }}>
                <a 
                  href={previewVideo.video_path} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: '#1890ff' }}
                >
                  在新窗口打开视频
                </a>
              </div>
            )}
            <div style={{ textAlign: 'left', marginTop: 20 }}>
              <p><strong>场景:</strong> {previewVideo.scene}</p>
              <p><strong>角色:</strong> {previewVideo.characters}</p>
              <p><strong>动作:</strong> {previewVideo.action}</p>
              <p><strong>台词:</strong> {previewVideo.dialog}</p>
              <p><strong>字幕:</strong> {previewVideo.subtitle}</p>
              <p><strong>景别:</strong> {previewVideo.camera}</p>
              <p><strong>运镜:</strong> {previewVideo.movement}</p>
              <p><strong>音效:</strong> {previewVideo.sound}</p>
              <p><strong>时长:</strong> {previewVideo.duration}秒</p>
            </div>
          </div>
        )}
      </Modal>

      {/* 视频上传模态框 */}
      <Modal
        title="上传视频"
        open={isUploadModalVisible}
        onOk={handleUpload}
        onCancel={handleCancelUpload}
        okText="上传"
        cancelText="取消"
        width={400}
      >
        {uploadingShot && (
          <div>
            <p style={{ marginBottom: 16 }}>为第{uploadingShot.index}镜上传视频</p>
            <div style={{ marginBottom: 20 }}>
              <input
                type="file"
                ref={fileInputRef}
                accept="video/*"
                onChange={handleFileChange}
              />
            </div>
            {uploadFile && (
              <div style={{ marginBottom: 20, color: '#52c41a' }}>
                已选择: {uploadFile.name}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#999' }}>
              支持的视频格式: MP4, AVI, MOV等
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StoryboardPage;
